import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Button as MantineButton, createStyles} from '@mantine/core';
import React from 'react';

import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {AI_BLUE_75} from './AIStyleConstants';

export enum AIButtonStyle {
  BLUE_75 = 'blue75',
}

const useStyles = createStyles(() => ({
  root: {
    fontSize: '13px',
    fontWeight: 400,
    padding: '4px 10px 5px',
  },
  noTextleftIcon: {
    marginRight: 0,
  },
  noTextRightIcon: {
    marginLeft: 0,
  },
  blue75: {
    backgroundColor: AI_BLUE_75,
    border: `1px solid ${Color.WHITE}`,
    color: Color.BLUE_500,
    '&:hover': {
      backgroundColor: Color.BLUE_50,
      color: Color.BLUE_500,
    },
    '&:focus': {
      color: Color.BLUE_600,
    },
    '&:active': {
      backgroundColor: Color.BLUE_100,
      color: Color.BLUE_500,
    },
    '&[data-disabled]': {
      backgroundColor: Color.GRAY_50,
      color: Color.GRAY_500,
    },
  },
  label: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  caret: {
    opacity: 0.4,
    paddingBottom: 1,
  },
}));

interface AIButtonProps {
  buttonStyle?: AIButtonStyle;
  text?: React.ReactNode;
  icon?: BenchlingIconName;
  ariaLabel?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isDropdown?: boolean;
  className?: string;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Mantine-based button matching the AI chat blue button visuals.
 * Intentionally kept local to chat-library until we decide broader adoption.
 */
export default function AIButton({
  buttonStyle = AIButtonStyle.BLUE_75,
  text,
  icon,
  ariaLabel,
  disabled = false,
  onClick,
  isDropdown = false,
  className,
  dataTestAttrs,
}: AIButtonProps): JSX.Element {
  const {classes, cx} = useStyles();
  const styleClassMap: Record<AIButtonStyle, string> = {
    [AIButtonStyle.BLUE_75]: classes.blue75,
  };
  const variantClassName = styleClassMap[buttonStyle];

  return (
    <MantineButton
      aria-label={ariaLabel}
      className={cx(classes.root, variantClassName, className)}
      classNames={{
        label: classes.label,
        leftIcon: text == null ? classes.noTextleftIcon : undefined,
        rightIcon: text == null ? classes.noTextRightIcon : undefined,
      }}
      leftIcon={icon != null ? <BenchlingIcon icon={icon} /> : undefined}
      rightIcon={isDropdown ? <BenchlingIcon className={classes.caret} icon="chevron-down" /> : undefined}
      disabled={disabled}
      onClick={onClick}
      compact={true}
      variant="filled"
      {...getTestProps(dataTestAttrs)}
    >
      {text}
    </MantineButton>
  );
}
